import { shopPageModuleQuery } from '@data/sanity/queries/page'
import { SanityShopPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext } from '@lib/routes'
import {
  defaultModuleQueryParameters,
  getShopIndexPage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ShopPageProps = BasePageProps & {
  page: SanityShopPage
  site: SanitySiteFragment
}

const ShopPage = ({ draftMode, locale, page, site }: ShopPageProps) => {
  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && (
        <Modules
          modules={page.modules}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopPage"
          quickFilterBasePath="/shop/all"
          quickFilterAllPath="/shop"
        />
      )}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={shopPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
          pageProducts={page.products}
          pageFeaturedProductIds={page.featuredProductIds}
          parentPageType="shopPage"
          quickFilterBasePath="/shop/all"
          quickFilterAllPath="/shop"
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<ShopPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const shopIndexPage = await getShopIndexPage(sanityClient, context.locale)

  if (!shopIndexPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(shopIndexPage.page),
      site: getNonNullValues(shopIndexPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default ShopPage
